import { DAYS } from "@repo/shared/utils/time";

const RUNS_DIRECTORY = "runs";
const ARROW_EXTENSION = ".arrow";

/**
 * Age after which a cached run payload is dropped by startup maintenance.
 * Age-based rather than reference-based on purpose: the set of "live" run ids
 * is deceptively hard to compute (`activeRunId` lags a dataset's `runId` while
 * a new run downloads, chat messages reference runs no dataset points at, and
 * public/shared board viewers never populate the dataset store), and a second
 * tab could evict a file this tab just registered.
 */
const RUN_CACHE_MAX_AGE_MS = 7 * DAYS;

/**
 * Multiplier applied when reserving space for an incoming payload, so a write
 * does not land with the disk exactly full.
 */
const SPACE_HEADROOM = 1.25;

const isOpfsSupported = (): boolean =>
  typeof navigator !== "undefined" &&
  "storage" in navigator &&
  "getDirectory" in navigator.storage;

/**
 * Whether the OPFS run cache is usable in this browser. Callers that stream a
 * payload need to know up front: without OPFS there is nowhere to stream to and
 * the bytes must be buffered in memory instead.
 */
export const isRunCacheSupported = (): boolean => isOpfsSupported();

/** Builds the OPFS filename for a run's immutable Arrow result. */
export const buildRunArrowFileName = (runId: string): string =>
  `${runId}${ARROW_EXTENSION}`;

const getRunsDirectory = async (options: {
  create: boolean;
}): Promise<FileSystemDirectoryHandle> => {
  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle(RUNS_DIRECTORY, { create: options.create });
};

/** A cached run payload: its OPFS handle plus a snapshot of the current file. */
export type CachedRunArrow = {
  handle: FileSystemFileHandle;
  file: File;
};

/**
 * Resolves a run's cached Arrow entry *without reading its contents*, so a
 * multi-hundred-MB payload can be handed to DuckDB as a file rather than a heap
 * buffer. Returns `null` when unavailable or when the entry is empty.
 *
 * Run results are immutable and content-addressed by id, so any hit is valid —
 * no version/etag comparison needed.
 */
export const getCachedRunArrow = async (
  runId: string,
): Promise<CachedRunArrow | null> => {
  if (!isOpfsSupported()) return null;
  try {
    const runsDir = await getRunsDirectory({ create: false });
    const handle = await runsDir.getFileHandle(buildRunArrowFileName(runId));
    const file = await handle.getFile();
    if (file.size === 0) return null;
    return { handle, file };
  } catch {
    return null;
  }
};

/** Returns true for a storage-quota rejection (both modern and legacy shapes). */
const isQuotaExceeded = (err: unknown): boolean =>
  err instanceof DOMException &&
  (err.name === "QuotaExceededError" || err.code === 22);

const writeRunArrow = async (
  runId: string,
  bytes: Uint8Array,
): Promise<void> => {
  const runsDir = await getRunsDirectory({ create: true });
  const fileHandle = await runsDir.getFileHandle(buildRunArrowFileName(runId), {
    create: true,
  });
  const writable = await fileHandle.createWritable();
  await writable.write(bytes as unknown as ArrayBuffer);
  await writable.close();
};

/**
 * Writes a run's Arrow payload to OPFS. No-op when unsupported.
 *
 * On a quota rejection the partial file is discarded, the oldest cached runs
 * are evicted to make room, and the write is retried once. A second failure
 * propagates — the caller can still register the bytes it holds, it just loses
 * the durable cache entry.
 */
export const writeRunArrowToOpfs = async (
  runId: string,
  bytes: Uint8Array,
): Promise<void> => {
  if (!isOpfsSupported()) return;
  await ensureRunCacheSpace({
    requiredBytes: bytes.byteLength,
    keepRunId: runId,
  });
  try {
    await writeRunArrow(runId, bytes);
  } catch (err) {
    if (!isQuotaExceeded(err)) throw err;
    await removeRunArrow(runId);
    await evictOldestRunArrows({
      keepRunId: runId,
      targetBytes: bytes.byteLength,
    });
    await writeRunArrow(runId, bytes);
  }
};

type RunCacheEntry = {
  fileName: string;
  runId: string;
  lastModified: number;
  size: number;
};

/** Lists cached run payloads with their size + mtime. Empty when unavailable. */
const listRunArrowEntries = async (): Promise<RunCacheEntry[]> => {
  const entries: RunCacheEntry[] = [];
  try {
    const runsDir = await getRunsDirectory({ create: false });
    for await (const [fileName, handle] of runsDir.entries()) {
      if (handle.kind !== "file") continue;
      if (!fileName.endsWith(ARROW_EXTENSION)) continue;
      try {
        const file = await (handle as FileSystemFileHandle).getFile();
        entries.push({
          fileName,
          runId: fileName.slice(0, -ARROW_EXTENSION.length),
          lastModified: file.lastModified,
          size: file.size,
        });
      } catch {
        // Skip entries that vanished or are locked by another tab.
      }
    }
  } catch {
    // OPFS cache has not been initialized yet.
  }
  return entries;
};

const removeRunArrow = async (runId: string): Promise<void> => {
  try {
    const runsDir = await getRunsDirectory({ create: false });
    await runsDir.removeEntry(buildRunArrowFileName(runId));
  } catch {
    // Already gone.
  }
};

/**
 * Deletes a run's cached payload. Used by callers that own the file's lifetime
 * outright (local uploads), rather than relying on age-based maintenance.
 */
export const removeRunArrowFromOpfs = async (runId: string): Promise<void> => {
  if (!isOpfsSupported()) return;
  await removeRunArrow(runId);
};

/**
 * Evicts oldest-first until at least `targetBytes` have been freed, never
 * touching `keepRunId` (the payload currently being written).
 */
const evictOldestRunArrows = async ({
  keepRunId,
  targetBytes,
}: {
  keepRunId: string;
  targetBytes: number;
}): Promise<{ freedBytes: number; removed: number }> => {
  const entries = (await listRunArrowEntries())
    .filter((entry) => entry.runId !== keepRunId)
    .sort((a, b) => a.lastModified - b.lastModified);

  let freedBytes = 0;
  let removed = 0;
  for (const entry of entries) {
    if (freedBytes >= targetBytes) break;
    await removeRunArrow(entry.runId);
    freedBytes += entry.size;
    removed += 1;
  }
  return { freedBytes, removed };
};

/**
 * Frees space for an incoming payload of `requiredBytes` by evicting the oldest
 * cached runs. Best-effort: browsers that do not implement `storage.estimate()`
 * fall back to the reactive quota handling on write.
 */
const ensureRunCacheSpace = async ({
  requiredBytes,
  keepRunId,
}: {
  requiredBytes: number;
  keepRunId: string;
}): Promise<void> => {
  if (requiredBytes <= 0) return;
  if (typeof navigator.storage.estimate !== "function") return;
  try {
    const { quota, usage } = await navigator.storage.estimate();
    if (quota == null || usage == null) return;
    const needed = requiredBytes * SPACE_HEADROOM;
    const available = quota - usage;
    if (available >= needed) return;
    await evictOldestRunArrows({
      keepRunId,
      targetBytes: needed - available,
    });
  } catch {
    // Quota introspection is advisory; the write path still handles overflow.
  }
};

/**
 * Streams a response body straight into the run's OPFS cache entry, so a large
 * payload never materializes in the JS heap. Returns the bytes written.
 *
 * `createWritable()` buffers into a swap file and only publishes on `close()`,
 * so an interrupted download leaves no partial cache entry.
 */
export const streamRunArrowToOpfs = async (
  runId: string,
  body: ReadableStream<Uint8Array>,
  options?: { expectedBytes?: number | null },
): Promise<number> => {
  const expectedBytes = options?.expectedBytes ?? 0;
  if (expectedBytes > 0) {
    await ensureRunCacheSpace({
      requiredBytes: expectedBytes,
      keepRunId: runId,
    });
  }

  const runsDir = await getRunsDirectory({ create: true });
  const fileHandle = await runsDir.getFileHandle(buildRunArrowFileName(runId), {
    create: true,
  });
  const writable = await fileHandle.createWritable();
  const reader = body.getReader();

  let byteLength = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      await writable.write(value as unknown as ArrayBuffer);
      byteLength += value.byteLength;
    }
    await writable.close();
  } catch (err) {
    await writable.abort().catch(() => undefined);
    await removeRunArrow(runId);
    // Free room so a retry of this download can succeed.
    if (isQuotaExceeded(err) && expectedBytes > 0) {
      await evictOldestRunArrows({
        keepRunId: runId,
        targetBytes: expectedBytes,
      });
    }
    throw err;
  }

  return byteLength;
};

/**
 * Drops cached run payloads older than `maxAgeMs`. Safe to call at startup
 * before any payload is registered: a run written in this session is never old
 * enough to be eligible.
 */
export const gcRunArrowByAge = async (
  maxAgeMs: number = RUN_CACHE_MAX_AGE_MS,
): Promise<{ removed: number }> => {
  if (!isOpfsSupported()) return { removed: 0 };
  const cutoff = Date.now() - maxAgeMs;
  const entries = await listRunArrowEntries();
  let removed = 0;
  for (const entry of entries) {
    if (entry.lastModified >= cutoff) continue;
    await removeRunArrow(entry.runId);
    removed += 1;
  }
  return { removed };
};
